import { useState } from 'react';
import { Menu } from '@shared/components/Menu';
import { TagField } from '@shared/data/entity/fields/TagField';
import {
  TagMultiSelectOptions,
  TagSingleSelectOptions,
} from '@shared/scenes/entity/components/fields/TagEditField';
import { TagComponent } from '@shared/scenes/workflows/config';
import { API } from '@shared/types/api';
import { SingleComponentFormProps } from './SingleComponentForm';
import styles from './TagComponentForm.module.css';

interface Props extends SingleComponentFormProps {
  component: TagComponent;
}

/**
 * The tag picker an input button opens. Mounted only while open: the menu opens on mount, and
 * closing it calls `onClose`. Picking a tag in a single-select list closes it.
 */
export function TagComponentForm({
  fieldName,
  field,
  onUpdateField,
  anchorName,
  component,
  onClose,
  loading,
}: Props) {
  const tagField = field as TagField | null;
  const tagOptions = component.options ?? [];
  const activeOptions = tagOptions.filter(option => !option.archived);
  const selectedValues = tagOptions
    .filter(option => tagField?.tagIds.includes(option.value))
    .map(option => option.value);
  const label = component.label ?? '';

  const onChange = (newValues: string[]) => {
    const newField = new TagField(
      newValues,
      component.id,
      tagOptions,
      component.multiselect ?? false,
      component.addAdditionalValues ?? false,
    );
    onUpdateField(fieldName, newField);
  };

  return (
    <Menu
      isOpen
      onOpenChange={isOpen => !isOpen && onClose()}
      anchorName={anchorName}
      closeOnSelect={false}
      align="center"
    >
      <Menu.Content mobileSheet className={styles.tagComponentFormMenu} aria-label={label}>
        <Menu.TitleBar title={label} endContent={<Menu.CloseButton />} mobileOnly />
        {loading && <Menu.Loading />}
        {!loading && (
          <TagOptions
            options={activeOptions}
            selectedValues={selectedValues}
            onChange={onChange}
            isMultiselect={component.multiselect ?? false}
            isRequired={component.required ?? false}
          />
        )}
      </Menu.Content>
    </Menu>
  );
}

function TagOptions({
  isMultiselect,
  ...props
}: {
  options: API.TagComponentOption[];
  selectedValues: string[];
  onChange: (values: string[]) => void;
  isMultiselect: boolean;
  isRequired: boolean;
}) {
  const [filter, setFilter] = useState('');
  const optionsProps = { ...props, filter, setFilter, canCreate: false };

  if (isMultiselect) {
    return <TagMultiSelectOptions {...optionsProps} selectAll={false} />;
  }
  return <TagSingleSelectOptions {...optionsProps} />;
}
